import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Calculator,
  Gift,
} from "lucide-react";

const milestones = [
  {
    referrals: "1 Referral",
    purchase: "₹200",
    total: "₹200",
  },
  {
    referrals: "5 Referrals",
    purchase: "₹200",
    total: "₹1,000",
  },
  {
    referrals: "10 Referrals",
    purchase: "₹200",
    total: "₹2,000",
  },
  {
    referrals: "50 Referrals",
    purchase: "₹200",
    total: "₹10,000",
  },
  {
    referrals: "100 Referrals",
    purchase: "₹200",
    total: "₹20,000",
  },
];

const rules = [
  "Referee must register using your verified referral link or code.",
  "Referee must complete the ₹1,000 software purchase with verified payment.",
  "₹200 is credited immediately after payment verification.",
  "Commissions is created only once per user purchase to prevent fraud.",
];

function ReferEarn() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-[#f6f8fb] text-slate-900">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-[#f6f8fb]">
        {/* Background glow */}
        <div className="pointer-events-none absolute -left-40 top-10 h-80 w-80 animate-[refrexFloat_8s_ease-in-out_infinite] rounded-full bg-emerald-200/30 blur-[120px]" />

        <div
          className="pointer-events-none absolute -right-40 top-20 h-96 w-96 animate-[refrexFloat_10s_ease-in-out_infinite_reverse] rounded-full bg-blue-100/40 blur-[130px]"
        />

        <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-slate-200" />

        <div
          className={`relative mx-auto max-w-7xl px-5 pb-10 pt-10 text-center transition-all duration-1000 sm:px-6 sm:pb-14 sm:pt-14 lg:px-8 lg:pt-16 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          {/* Refrex Brand */}
          <div className="mb-5 inline-flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#00a96d] shadow-md shadow-emerald-200">
              <span className="text-xs font-black text-white">R</span>
            </div>

            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
              Referx
            </span>
          </div>

          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
            Referral Partnership
          </p>

          <h1 className="mt-3 text-4xl font-black leading-tight tracking-[-0.05em] text-[#08111f] sm:text-5xl lg:text-6xl">
            The ₹200 Referral Program
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            Refer qualifying users to the WhatsApp Automation Software and
            earn ₹200 for every verified purchase.
          </p>
        </div>
      </section>

      {/* ================= MAIN CONTENT ================= */}
      <section className="relative">
        <div className="pointer-events-none absolute left-1/2 top-20 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-100/30 blur-[130px]" />

        <div className="relative mx-auto max-w-6xl px-5 pb-14 sm:px-6 sm:pb-20 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
            {/* ================= RULES CARD ================= */}
            <div
              className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.055)] transition-all duration-700 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_24px_60px_rgba(15,23,42,0.09)] ${
                loaded
                  ? "translate-y-0 opacity-100"
                  : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              {/* Top animated line */}
              <div className="absolute left-0 right-0 top-0 h-1 origin-left scale-x-0 bg-[#00a96d] transition-transform duration-700 group-hover:scale-x-100" />

              <div className="p-6 sm:p-8 lg:p-9">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-all duration-300 group-hover:scale-110 group-hover:bg-emerald-100">
                    <CheckCircle2 size={20} strokeWidth={2} />
                  </div>

                  <h2 className="text-xl font-black tracking-[-0.03em] text-[#08111f] sm:text-2xl">
                    Commission Qualification Rules
                  </h2>
                </div>

                {/* Rules */}
                <div className="mt-7 space-y-5">
                  {rules.map((rule, index) => (
                    <div
                      key={rule}
                      className="group/rule flex gap-3 transition-all duration-300 hover:translate-x-1"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />

                      <p className="text-sm leading-6 text-slate-500">
                        {rule}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Small earning highlight */}
                <div className="mt-8 flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm">
                    <Gift size={19} />
                  </div>

                  <div>
                    <div className="text-xs font-black text-slate-800">
                      Verified Referral
                    </div>

                    <div className="mt-0.5 text-[10px] text-slate-500">
                      ₹200 commission per verified purchase
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= MILESTONE TABLE ================= */}
            <div
              className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.055)] transition-all duration-700 hover:-translate-y-1 hover:border-purple-200 hover:shadow-[0_24px_60px_rgba(15,23,42,0.09)] ${
                loaded
                  ? "translate-y-0 opacity-100"
                  : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: "350ms" }}
            >
              {/* Top animated line */}
              <div className="absolute left-0 right-0 top-0 h-1 origin-right scale-x-0 bg-purple-500 transition-transform duration-700 group-hover:scale-x-100" />

              <div className="p-6 sm:p-8 lg:p-9">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 transition-all duration-300 group-hover:scale-110 group-hover:bg-purple-100">
                    <Calculator size={20} strokeWidth={2} />
                  </div>

                  <h2 className="text-xl font-black tracking-[-0.03em] text-[#08111f] sm:text-2xl">
                    Earning Milestones Table
                  </h2>
                </div>

                {/* Table */}
                <div className="mt-7 overflow-hidden rounded-2xl border border-slate-100">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wide text-slate-500 sm:px-5">
                          Referrals
                        </th>

                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wide text-slate-500 sm:px-5">
                          Per Purchase
                        </th>

                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wide text-slate-500 sm:px-5">
                          Total Credited
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {milestones.map((item, index) => (
                        <tr
                          key={item.referrals}
                          className="group/row border-t border-slate-100 transition-colors duration-200 hover:bg-emerald-50/50"
                        >
                          <td className="px-4 py-3.5 text-xs font-semibold text-slate-600 sm:px-5">
                            {item.referrals}
                          </td>

                          <td className="px-4 py-3.5 text-xs font-medium text-slate-600 sm:px-5">
                            {item.purchase}
                          </td>

                          <td className="px-4 py-3.5 text-xs font-black text-emerald-600 sm:px-5">
                            {item.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* ================= CTA ================= */}
          <div
            className={`mt-10 flex justify-center transition-all duration-1000 ${
              loaded
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "550ms" }}
          >
            <Link
              to="/signup"
              className="group inline-flex min-h-12 items-center justify-center rounded-xl bg-[#00a96d] px-7 py-3 text-sm font-black text-white shadow-lg shadow-emerald-200 transition-all duration-300 hover:-translate-y-1 hover:bg-[#00945f] hover:shadow-xl hover:shadow-emerald-200"
            >
              Join Platform & Start Referring

              <ArrowRight
                size={16}
                className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Animations */}
      <style>{`
        @keyframes refrexFloat {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(0, -18px, 0) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}

export default ReferEarn;